import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isNullOrUndefined } from '@sla-ng/core';
import { BehaviorSubject, filter, Observable, take } from 'rxjs';
import { ISharedDataState } from '../../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../../services/facades/master-data/shared-data.facade';
import { IPlannedPurchasePriceState } from '../../../../services/facades/planning/planned-purchase-price-state.model';
import { PlannedPurchasePriceFacade } from '../../../../services/facades/planning/planned-purchase-price.facade';
import { IPlannedPurchasePrice } from '../../../../models/planned-purchase-price.model';
import { IPlannedPurchasePriceDialogLayout } from '../../../../models/layout/planned-purchase-price-dialog-layout.model';
import { PlannedPurchasePriceLayoutService } from '../../../../services/layout/planned-purchase-price-layout.service';
import { DateRange } from '@angular/material/datepicker';
import * as moment from 'moment';
import { IAddPurchasePricePlanning } from '../../../../models/add-planned-purchase-price.model';
import { stringToMoment } from '../../../../utils/moment-utils';

@Component({
    templateUrl: './planned-purchase-price-dialog.component.html',
    styleUrls: [ './planned-purchase-price-dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlannedPurchasePriceDialogComponent implements OnInit {
    public state$: Observable<IPlannedPurchasePriceState>;
    public dialogFieldsValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public formGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);
    public layout: IPlannedPurchasePriceDialogLayout;
    public initialData: BehaviorSubject<{ dateRange: DateRange<moment.Moment>, priceEuro: number, articleId: number }>
        = new BehaviorSubject<{ dateRange: DateRange<moment.Moment>, priceEuro: number, articleId: number }>({
            dateRange: undefined,
            priceEuro: undefined,
            articleId: undefined,
        },
        );

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IPlannedPurchasePrice,
        private myMatDialogRef: MatDialogRef<PlannedPurchasePriceDialogComponent>,
        private myPlannedPurchasePriceFacade: PlannedPurchasePriceFacade,
        private mySharedDataFacade: SharedDataFacade,
        private myLayoutService: PlannedPurchasePriceLayoutService,
    ) {
    }

    public ngOnInit(): void {
        this.state$ = this.myPlannedPurchasePriceFacade.subState();
        if (!isNullOrUndefined(this.data) && !isNullOrUndefined(this.data.id)) {
            this.layout = this.myLayoutService.getUpdateDialogLayout();
            this.initialData.next({
                dateRange: new DateRange(stringToMoment(this.data.validFrom), stringToMoment(this.data.validTo)),
                priceEuro: this.data.priceCt / 100,
                articleId: this.data.articleId,
            });
            this.selectMeasureUnit(this.data.articleId);
        } else {
            this.layout = this.myLayoutService.getAddDialogLayout();
        }
        if (!isNullOrUndefined(this.data.validFrom) && !isNullOrUndefined(this.data.validTo)) {
            this.formGroup$.asObservable()
                .pipe(
                    filter(value => !isNullOrUndefined(value)),
                    take(1),
                ).subscribe(() => {
                    this.formGroup$.value.controls[ 'dateRange' ].patchValue({ start: this.data.validFrom, end: this.data.validTo }, { emitEvent: false });
                });
        }
    }

    public async save(): Promise<void> {
        const data: IAddPurchasePricePlanning = this.transformDialogData(this.formGroup$.value.getRawValue());
        if (isNullOrUndefined(this.data.id)) {
            await this.myPlannedPurchasePriceFacade.addPlannedPurchasePrice(data);
        } else {
            await this.myPlannedPurchasePriceFacade.updatePlannedPurchasePrice(this.data.id, data);
        }
        this.formGroup$.value.patchValue({ priceEuro: null, articleId: null });
    }

    public close(): void {
        this.myMatDialogRef.close(false);
    }

    public onFormGroupChange(formGroup: FormGroup): void {
        formGroup.valueChanges.subscribe((data: { dateRange: DateRange<moment.Moment>, priceEuro: number, articleId: number }) => {
            this.validateDialogFields(this.transformDialogData(data));
        });
        formGroup.controls[ 'articleId' ].valueChanges.subscribe((articleId: number) => {
            this.selectMeasureUnit(articleId);
        });
        this.formGroup$.next(formGroup);
    }

    private validateDialogFields(plannedPurchasePrice: IAddPurchasePricePlanning): void {
        // Validations to disable saving/editing
        if (isNullOrUndefined(plannedPurchasePrice.articleId)
            || isNullOrUndefined(plannedPurchasePrice.priceCt)
            || plannedPurchasePrice.priceCt < 0
            || isNullOrUndefined(plannedPurchasePrice.from)
            || isNullOrUndefined(plannedPurchasePrice.to)
            || plannedPurchasePrice.to.isBefore(plannedPurchasePrice.from)) {
            this.dialogFieldsValid$.next(false);
        } else {
            this.dialogFieldsValid$.next(true);
        }
    }

    private transformDialogData(data: { dateRange: DateRange<moment.Moment | string>, priceEuro: number | string, articleId: number }):
    IAddPurchasePricePlanning {
        return {
            articleId: isNullOrUndefined(data.articleId) ? this.data.articleId : data.articleId,
            priceCt: typeof data.priceEuro === 'string' ? undefined : 100 * data.priceEuro,
            from: stringToMoment(data.dateRange.start),
            to: stringToMoment(data.dateRange.end),
        };
    }

    private selectMeasureUnit(purchaseArticleId: number): void {
        const sharedDataState: ISharedDataState = this.mySharedDataFacade.getStateSnapshot();
        const measureUnitId: number = sharedDataState.measureUnitLookup[ sharedDataState.articleLookup[ purchaseArticleId ]?.baseMeasureUnitId ]?.id;
        // Wait until the formgroup is initialized, then set the measureUnit
        this.formGroup$.asObservable()
            .pipe(
                filter(value => !isNullOrUndefined(value)),
                take(1),
            ).subscribe(() => {
                this.formGroup$.value.controls[ 'measureUnitId' ].patchValue(measureUnitId, { emitEvent: false });
            });

    }
}
